/**
 * CREATE WORKSPACE (A05)
 *
 * Form to create a new workspace (empresa).
 * Design ref: A05CreateWorkspace in screens-auth.jsx
 * Fields: name, RNC, industria, pais, moneda, subdomain.
 * 2-col grids: [RNC | Industria], [Pais | Moneda]
 */

import React, { useState, FormEvent, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { useDynamicBranding } from '../hooks/useDynamicBranding';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/ui/shadcn/ui/select';
import { Loader2 } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
import { handleHandoffRedirect, setSSOCookie } from './Login';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import { notifyCoreAPILogin } from '@shared/services/coreAuthService';
import { APP_SSO_CONFIGS } from '@shared/navigation/appSSOConfig';

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

const getCoreApiUrl = (): string => {
  const envUrl = (import.meta.env.VITE_CORE_API_URL || '').trim();
  if (envUrl) return envUrl;
  return import.meta.env.DEV ? 'http://localhost:4000' : 'https://api.elfom.com';
};

const BUSINESS_TYPES = [
  { value: 'tapiceria', label: 'Tapiceria y restauracion' },
  { value: 'tienda_celulares', label: 'Tienda de Celulares' },
  { value: 'construccion', label: 'Construccion y materiales' },
  { value: 'retail', label: 'Retail y punto de venta' },
  { value: 'servicios', label: 'Servicios profesionales' },
  { value: 'muebleria', label: 'Muebleria' },
  { value: 'otro', label: 'Otro tipo de negocio' },
] as const;

const COUNTRIES = [
  { value: 'DO', label: 'Republica Dominicana' },
  { value: 'MX', label: 'Mexico' },
  { value: 'CO', label: 'Colombia' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'PE', label: 'Peru' },
  { value: 'AR', label: 'Argentina' },
] as const;

const CURRENCIES = [
  { value: 'DOP', label: 'DOP — Peso dominicano' },
  { value: 'MXN', label: 'MXN — Peso mexicano' },
  { value: 'USD', label: 'USD — Dolar' },
  { value: 'COP', label: 'COP — Peso colombiano' },
] as const;

const CreateWorkspacePage: React.FC = () => {
  const [params] = useSearchParams();
  const app = params.get('app') || 'empresa';
  const returnTo = params.get('returnTo');
  const clientOriginParam = params.get('clientOrigin');

  useDynamicBranding(app);
  const branding = getBrandingConfig(app);

  const [name, setName] = useState('');
  const [rnc, setRnc] = useState('');
  const [tipo, setTipo] = useState('');
  const [pais, setPais] = useState('DO');
  const [moneda, setMoneda] = useState('DOP');
  const [subdomain, setSubdomain] = useState('');
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!subdomainTouched) {
      setSubdomain(slugify(name));
    }
  }, [name, subdomainTouched]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('El nombre del workspace es requerido');
      return;
    }
    if (!tipo) {
      setError('Selecciona el tipo de negocio');
      return;
    }
    if (!subdomain.trim()) {
      setError('El subdominio es requerido');
      return;
    }

    setLoading(true);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        setError('No hay sesion activa. Por favor inicia sesion primero.');
        setLoading(false);
        return;
      }

      const coreApiUrl = getCoreApiUrl();
      const response = await fetch(`${coreApiUrl}/workspaces/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          tipo,
          rnc: rnc.trim() || undefined,
          pais,
          moneda,
          subdomain: subdomain.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        setError(errorData.message || errorData.error || 'Error al crear el workspace');
        setLoading(false);
        return;
      }

      try {
        localStorage.setItem('workspace_name', name.trim());
        localStorage.setItem('workspace_tipo', tipo);
      } catch (_) {}

      try {
        await notifyCoreAPILogin(session.access_token);
        await setSSOCookie(session.access_token);
      } catch (_) {}

      if (app && APP_SSO_CONFIGS[app]) {
        const trace = createSsoTrace({ app: 'accounts', feature: 'SSO_HANDOFF' });
        trace.step('CREATE_WORKSPACE_SUCCESS', { name: name.trim(), tipo, subdomain: subdomain.trim() });

        const handoffReturnTo = '/onboarding/fiscal';
        const ok = await handleHandoffRedirect(
          app,
          handoffReturnTo,
          (msg) => setError(msg),
          trace,
          clientOriginParam,
        );
        if (ok) return;
      }

      window.location.href = returnTo || '/onboarding/fiscal';
    } catch (err: any) {
      setError(err?.message || 'Error inesperado. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full flex flex-col gap-5" style={{ maxWidth: 520 }}>
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            Crea tu workspace.
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5" style={{ lineHeight: 1.55, maxWidth: '44ch' }}>
            Un workspace = una empresa. Aqui viven tus clientes, ordenes, facturas y equipo. Lo configuras una sola vez.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Nombre de la empresa */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground" htmlFor="ws-name">
              Nombre de la empresa
            </label>
            <Input
              id="ws-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Tapiceria Reyna"
              required
              disabled={loading}
              className="h-10 text-sm"
            />
            <p className="text-[11.5px] text-muted-foreground">
              Sera visible para tu equipo y clientes en cotizaciones.
            </p>
          </div>

          {/* RNC + Industria (2-col grid) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground" htmlFor="ws-rnc">
                RNC / RFC / RUC
              </label>
              <Input
                id="ws-rnc"
                type="text"
                value={rnc}
                onChange={(e) => setRnc(e.target.value)}
                placeholder="Identificacion fiscal"
                disabled={loading}
                className="h-10 text-sm font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground" htmlFor="ws-tipo">
                Industria
              </label>
              <Select value={tipo} onValueChange={setTipo} disabled={loading}>
                <SelectTrigger id="ws-tipo" className="w-full h-10 text-sm">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((bt) => (
                    <SelectItem key={bt.value} value={bt.value}>
                      {bt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pais + Moneda (2-col grid) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground" htmlFor="ws-pais">
                Pais
              </label>
              <Select value={pais} onValueChange={setPais} disabled={loading}>
                <SelectTrigger id="ws-pais" className="w-full h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground" htmlFor="ws-moneda">
                Moneda
              </label>
              <Select value={moneda} onValueChange={setMoneda} disabled={loading}>
                <SelectTrigger id="ws-moneda" className="w-full h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subdomain */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground" htmlFor="ws-subdomain">
              Subdominio
            </label>
            <div className="flex items-stretch">
              <Input
                id="ws-subdomain"
                type="text"
                value={subdomain}
                onChange={(e) => {
                  setSubdomainTouched(true);
                  setSubdomain(slugify(e.target.value));
                }}
                placeholder="mi-empresa"
                required
                disabled={loading}
                className="h-10 text-sm font-mono rounded-r-none border-r-0"
              />
              <div className="flex items-center px-3 bg-muted border border-border border-l-0 rounded-r-md text-[11px] text-muted-foreground font-mono whitespace-nowrap">
                .theplatform360.com
              </div>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading || !name.trim() || !tipo || !subdomain.trim()}
            className="w-full h-11 mt-1"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creando...
              </>
            ) : (
              'Crear workspace y continuar →'
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground">
          Podras cambiar todos estos datos en Settings cuando quieras.
          <br />
          <a
            href={`/choose-path?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
            className="text-primary hover:underline font-medium mt-1 inline-block"
          >
            ← Volver
          </a>
        </div>
      </div>
    </AuthLayout>
  );
};

export default CreateWorkspacePage;
